import React, {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {Link} from "react-router-dom";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Ban, CalendarClock, Loader, Printer, TicketPercent, X} from "lucide-react";
import Button from "../Common/Button";
import {
    SearchInput, ToolbarSelect, ListLoader, ListEmpty, ListCard, thClass, mobileListClass, mobileRowClass, RowActions, Pagination
} from "../../stock/StockUI";
import toast from "react-hot-toast";
import secureStorage from "../../utils/secureStorage";
import {printCreditNote} from "./printCreditNote";
import {
    CREDIT_NOTE_STATUS_CLASSES,
    CREDIT_NOTE_STATUS_LABELS,
    apiErrorMessage,
    cancelCreditNote,
    fetchCreditNote,
    fetchCreditNotes,
    formatDate,
    formatDateTime,
    formatMoney,
    inputClass,
    setCreditNoteExpiry,
} from "./returnsApi";
import DateInput from "../Common/DateInput";

const PAGE_SIZE = 20;

// Portal: see ReturnDetailModal (transformed page ancestor vs `fixed`).
const Modal = ({title, onClose, children, footer}) => createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md max-h-[90vh] flex flex-col"
             onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                <Button variant="ghost" size="sm" icon={X} title="Fermer" onClick={onClose}/>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">{children}</div>
            {footer && <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">{footer}</div>}
        </div>
    </div>,
    document.body
);


const StatusBadge = ({status}) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${CREDIT_NOTE_STATUS_CLASSES[status]}`}>
        {CREDIT_NOTE_STATUS_LABELS[status]}
    </span>
);

// ─── Détail ──────────────────────────────────────────────────────────────────

const Field = ({label, children}) => (
    <div className="flex justify-between gap-4 text-sm">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className="text-right text-gray-900 dark:text-gray-100">{children}</span>
    </div>
);

const CreditNoteDetailModal = ({id, onClose}) => {
    const {data: note, isLoading} = useQuery({
        queryKey: ["credit-note", id],
        queryFn: () => fetchCreditNote(id),
    });

    return (
        <Modal title={note ? `Avoir ${note.code}` : "Avoir"} onClose={onClose}>
            {isLoading || !note ? (
                <div className="flex items-center gap-2 text-sm text-gray-500"><Loader className="w-4 h-4 animate-spin"/> Chargement…</div>
            ) : (
                <>
                    <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatMoney(note.amount)}</span>
                        <StatusBadge status={note.status}/>
                    </div>
                    <div className="space-y-2">
                        <Field label="Émis le">{formatDateTime(note.created_at)}</Field>
                        <Field label="Par">{[note.issued_by, note.issued_device_name].filter(Boolean).join(" · ") || "—"}</Field>
                        <Field label="Expire le">{note.expires_at ? formatDate(note.expires_at) : "Jamais"}</Field>
                        <Field label="Client">
                            {note.client
                                ? <Link to={`/clients/edit/${note.client.id}`} className="text-primary hover:underline">{note.client.name || "Sans nom"}</Link>
                                : "Au porteur"}
                        </Field>
                        {note.parent && <Field label="Reste de l'avoir"><span className="font-mono">{note.parent.code}</span></Field>}
                        {note.status === "used" && (
                            <>
                                <Field label="Utilisé le">{formatDateTime(note.used_at)}</Field>
                                <Field label="Montant utilisé">{formatMoney(note.used_amount)}</Field>
                                {note.used_by && <Field label="Par">{note.used_by}</Field>}
                            </>
                        )}
                        {note.remainder && (
                            <Field label="Reste réémis">
                                <span className="font-mono">{note.remainder.code}</span> · {formatMoney(note.remainder.amount)}
                                {" "}({CREDIT_NOTE_STATUS_LABELS[note.remainder.status]})
                            </Field>
                        )}
                        {note.note && <Field label="Note">{note.note}</Field>}
                    </div>
                </>
            )}
        </Modal>
    );
};

// ─── Liste ───────────────────────────────────────────────────────────────────

const CreditNotesTab = ({canManage}) => {
    const queryClient = useQueryClient();
    const [status, setStatus] = useState("active");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [detailId, setDetailId] = useState(null);
    const [toCancel, setToCancel] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [toExtend, setToExtend] = useState(null);
    const [newExpiry, setNewExpiry] = useState("");
    const [neverExpires, setNeverExpires] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [search]);

    const {data, isLoading, isError, isFetching} = useQuery({
        queryKey: ["credit-notes", status, debouncedSearch, page],
        queryFn: () => fetchCreditNotes({status, search: debouncedSearch, page, itemsPerPage: PAGE_SIZE}),
        placeholderData: keepPreviousData,
    });

    const refresh = () => {
        queryClient.invalidateQueries({queryKey: ["credit-notes"]});
        queryClient.invalidateQueries({queryKey: ["credit-note"]});
    };

    const cancelMutation = useMutation({
        mutationFn: cancelCreditNote,
        onSuccess: () => {
            toast.success("Avoir annulé");
            setToCancel(null);
            refresh();
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible d'annuler l'avoir")),
    });
    const expiryMutation = useMutation({
        mutationFn: setCreditNoteExpiry,
        onSuccess: () => {
            toast.success("Validité de l'avoir mise à jour");
            setToExtend(null);
            refresh();
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible de modifier la validité")),
    });

    const handlePrint = async (note) => {
        const account = await secureStorage.getItem("selectedAccount");
        if (!printCreditNote({note, shopName: account?.name || ""})) {
            toast.error("Autorisez les fenêtres pop-up pour imprimer l'avoir");
        }
    };

    const openExtend = (note) => {
        setToExtend(note);
        setNeverExpires(!note.expires_at);
        setNewExpiry(note.expires_at ? note.expires_at.slice(0, 10) : "");
    };

    const items = data?.items || [];
    const total = data?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    // Expired notes can be extended too (the server only needs status = active).
    const isExtendable = (note) => note.status === "active" || note.status === "expired";

    const noteActions = (note) => [
        ...(note.status === "active"
            ? [{label: "Réimprimer l'avoir", icon: Printer, onClick: () => handlePrint(note)}] : []),
        ...(canManage && isExtendable(note) ? [
            {label: "Modifier la validité", icon: CalendarClock, tone: "green", onClick: () => openExtend(note)},
            {label: "Annuler l'avoir", icon: Ban, tone: "red", danger: true, onClick: () => {
                setToCancel(note);
                setCancelReason("");
            }},
        ] : []),
    ];

    return (
        <div className="max-w-full">
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={search} onChange={setSearch} placeholder="Rechercher par code, nom ou téléphone du client..."/>
                <ToolbarSelect label="Statut" value={status} onChange={(value) => {
                    setStatus(value);
                    setPage(1);
                }}>
                    <option value="">Tous les statuts</option>
                    {Object.entries(CREDIT_NOTE_STATUS_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </ToolbarSelect>
            </div>

            {isLoading ? (
                <ListLoader label="Chargement des avoirs..."/>
            ) : (
                <ListCard>
                    {isError ? (
                        <p className="text-sm text-red-500 p-6 text-center">Impossible de charger les avoirs.</p>
                    ) : items.length === 0 ? (
                        <ListEmpty icon={TicketPercent} title="Aucun avoir trouvé"
                                   text="Aucun avoir ne correspond à votre recherche ou au statut choisi."/>
                    ) : (<div className={isFetching ? "opacity-60 transition-opacity" : "transition-opacity"}>
                        {/* Desktop Table */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className={thClass}>Code</th>
                                    <th className={`${thClass} text-right`}>Montant</th>
                                    <th className={thClass}>Statut</th>
                                    <th className={thClass}>Client</th>
                                    <th className={thClass}>Émis le</th>
                                    <th className={thClass}>Expire le</th>
                                    <th className={`${thClass} text-right`}>Actions</th>
                                </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {items.map((note) => (
                                    <tr key={note.id} onClick={() => setDetailId(note.id)}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                        <td className="px-6 py-4 font-mono text-sm font-semibold text-primary">{note.code}</td>
                                        <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                            {formatMoney(note.amount)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={note.status}/></td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{note.client?.name || "Au porteur"}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(note.created_at)}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                            {note.expires_at ? formatDate(note.expires_at) : "Jamais"}
                                        </td>
                                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                                            {noteActions(note).length > 0 && <RowActions actions={noteActions(note)}/>}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        {/* Mobile rows — edge to edge, same as the Stock / catalogue lists */}
                        <div className={mobileListClass}>
                            {items.map((note) => (
                                <div key={note.id} onClick={() => setDetailId(note.id)} className={`${mobileRowClass} cursor-pointer`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-mono text-base font-semibold text-primary">{note.code}</p>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">{note.client?.name || "Au porteur"}</p>
                                        </div>
                                        <div onClick={(e) => e.stopPropagation()}>
                                            {noteActions(note).length > 0 && <RowActions actions={noteActions(note)}/>}
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-3 mt-3">
                                        <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatMoney(note.amount)}</span>
                                        <StatusBadge status={note.status}/>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Émis le {formatDate(note.created_at)} · expire {note.expires_at ? `le ${formatDate(note.expires_at)}` : "jamais"}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>)}
                </ListCard>
            )}

            {!isLoading && !isError && items.length > 0 && (
                <Pagination page={page} totalPages={totalPages} onPage={setPage} summary={`${total} avoir(s)`}/>
            )}

            {detailId && <CreditNoteDetailModal id={detailId} onClose={() => setDetailId(null)}/>}

            {toCancel && (
                <Modal
                    title={`Annuler l'avoir ${toCancel.code} ?`}
                    onClose={() => setToCancel(null)}
                    footer={<>
                        <Button variant="secondary" onClick={() => setToCancel(null)}>Retour</Button>
                        <Button variant="dangerSolid" loading={cancelMutation.isPending}
                                onClick={() => cancelMutation.mutate({id: toCancel.id, reason: cancelReason})}>
                            Annuler l'avoir
                        </Button>
                    </>}
                >
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        L'avoir de {formatMoney(toCancel.amount)} ne pourra plus être utilisé en caisse. Cette action est définitive.
                    </p>
                    <input value={cancelReason} maxLength={255} onChange={(e) => setCancelReason(e.target.value)}
                           placeholder="Raison (facultatif)" className={inputClass}/>
                </Modal>
            )}

            {toExtend && (
                <Modal
                    title={`Validité de l'avoir ${toExtend.code}`}
                    onClose={() => setToExtend(null)}
                    footer={<>
                        <Button variant="secondary" onClick={() => setToExtend(null)}>Annuler</Button>
                        <Button variant="primary" loading={expiryMutation.isPending}
                                disabled={!neverExpires && !newExpiry}
                                onClick={() => expiryMutation.mutate({id: toExtend.id, expiresAt: neverExpires ? null : newExpiry})}>
                            Enregistrer
                        </Button>
                    </>}
                >
                    <label className="block">
                        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Valable jusqu'au</span>
                        <DateInput value={newExpiry} disabled={neverExpires}
                               onChange={(e) => setNewExpiry(e.target.value)} className={inputClass}/>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={neverExpires} onChange={(e) => setNeverExpires(e.target.checked)}
                               className="rounded border-gray-300 text-primary focus:ring-primary"/>
                        Sans date d'expiration
                    </label>
                </Modal>
            )}
        </div>
    );
};

export default CreditNotesTab;
